import React, {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  VoiceAgentContext,
  VoiceAgent,
  ConnectionConfig,
  AgentConfig,
  InputOptions,
  Channel,
  useAgentMessage,
  StringToAny,
  useInputModeToggleAgent,
} from "rapida-react";
import { AudioMessagingAction } from "./components/audio-messaging-action.tsx";
import { SimpleMessagingAction } from "./components/simple-messaging-action.tsx";
import EnhancedMessages from "./components/Messages.tsx"; 
import { color } from "framer-motion";
const CustomUsecaseForm = lazy(
  () => import("../../components/CustomUsecaseForm/CustomUsecaseForm.js")
);

// Enhanced loading fallback with better styling
const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      py: { xs: 6, sm: 8, md: 10 },
      minHeight: "200px",
    }}
  >
    <CircularProgress size={36} />
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mt: 2, opacity: 0.8 }}
    >
      Loading...
    </Typography>
  </Box>
);

interface LazyComponentProps {
  children: ReactNode;
  threshold?: number;
}

// Enhanced lazy loading component with better placeholder
const LazyComponent: React.FC<LazyComponentProps> = ({
  children,
  threshold = 0.1,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: "200px" }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return (
    <div ref={ref}>
      {isVisible ? (
        children
      ) : (
        <Box
          sx={{
            height: { xs: "150px", sm: "180px", md: "200px" },
            width: "100%",
            transition: "height 0.3s ease",
          }}
        />
      )}
    </div>
  );
};

const DemoTrialPage: React.FC = () => {
  useEffect(() => {
    // Preload critical components
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => {
        import("../../components/KeyFeatures/KeyFeatures.js");
        import("../../components/UseCases/UseCase.js");
      });
    }

    // Load non-critical components when tab is visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        import("../../components/CompliantFold/Compliant.js");
        import("../../components/IntegrationSupport/IntegrationSupport.js");
        import("../../components/CustomUsecaseForm/CustomUsecaseForm.js");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Styles object for inline CSS
  const styles = {
    mainContainer: {
      position: "relative",
      width: "100%",
      overflow: "hidden",
      backgroundColor: "black" // Dark background
    },
    chatContainer: {
      background: "rgba(26, 26, 26, 0.85)", // Darker, more opaque for better readability
      backdropFilter: "blur(10px)",
      height: "85vh",
      margin: "0 auto",
      borderRadius: "1.5rem",
      color: "#f0f0f0", // Light text for dark mode
      padding: "0 1rem",
      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(0, 0, 0, 0.15)", // Stronger shadow for depth
      transition: "all 0.3s ease",
      marginTop: "6rem",
      marginBottom: "1rem",
      border: "1px solid rgba(50, 50, 50, 0.5)" // Subtle border for definition
    },
    mainLayout: {
      height: "100%",
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "stretch",
    },
    columnLayout: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      minWidth: 0,
      flex: 1,
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 24px",
      borderBottom: "1px solid #333333",
      backgroundColor: "rgba(30, 30, 30, 0.6)"
    },
    headerTitle: {
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    avatar: {
      backgroundColor: "#e95f43",
      width: 38,
      height: 38,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      color: "#fff",
      fontWeight: 600,
      fontSize: "16px",
      boxShadow: "0 2px 8px rgba(233, 95, 67, 0.25)" // Glow effect for primary color
    }
  } as const;

  return (
    <Box sx={styles.mainContainer}>
      {/* Add the rapida react chat flow here */}
      <div style={styles.chatContainer}>
        <VoiceAgentContext.Provider
          value={
            new VoiceAgent(
              new ConnectionConfig(
                ConnectionConfig.WithSDK({
                  // api key from rapida credentials
                  apiKey:
                    "fe0727dd45acf2efce0752c4ff0783f232e559575bbfca059365333f2df2819a",
                  userId: "random-user / identified-user",
                })
              ).withConnectionCallback({
                onDisconnect: () => {
                  // do what you want when finished
                  console.log("disconnect");
                },
                onConnect() {
                  console.log("connected");
                },
                onError() {
                  console.log("error");
                },
              }),
              new AgentConfig(
                // replace this with actual agent id from rapida console
                "2181672431515598848",
                // you can select only Audio/ Text
                new InputOptions([Channel.Audio, Channel.Text], Channel.Text)
              )
                .withAgentCallback({
                  onStart: () => {
                    console.log("onStart: ()");
                  },
                  onComplete: () => {
                    console.log("onComplete:");
                  },
                  onTranscript: () => {
                    console.log("onTranscript");
                  },
                  onInterrupt: () => {
                    console.log("onInterrupt");
                  },
                  onGeneration: () => {
                    console.log("onGeneration");
                  },
                  onCompleteGeneration: () => {
                    console.log("onCompleteGeneration");
                  },
                  onStartConversation: () => {
                    console.log("onStartConversation");
                  },
                  onCompleteConversation: () => {
                    console.log("onCompleteConversation");
                  },
                  onMessage: () => {
                    console.log("onMessage: ()");
                  },
                })
                .addKeywords([
                  "dictionary - which you want the model to speak clearly",
                ])
                .addArgument("name", "<name>")
                .addMetadata("utm_1", StringToAny("utm_X"))
            )
          }
        >
          {/* { Your code goes here} */}
          <div style={styles.mainLayout}>
            <div style={styles.columnLayout}>
              <EnhancedMessages />
              <MessagingAction />
            </div>
          </div>
        </VoiceAgentContext.Provider>
      </div>

      {/* Contact Form Section */}
      <Box>
        <LazyComponent>
          <Suspense fallback={<LoadingFallback />}>
            <CustomUsecaseForm />
          </Suspense>
        </LazyComponent>
      </Box>
    </Box>
  );
};

export const MessagingAction: React.FC = () => {
  const { channel } = useInputModeToggleAgent();

  // Styles for MessagingAction component
  const styles = {
    container: {
      padding: "0.75rem 0",
    },
  } as const;

  return (
    <div style={styles.container}>
      {channel === Channel.Audio ? (
        <AudioMessagingAction />
      ) : (
        <SimpleMessagingAction />
      )}
    </div>
  );
};

export default DemoTrialPage;